type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  method?: HttpMethod;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  authToken?: string;
  signal?: AbortSignal;
}

export async function requestJson<T>(input: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = "GET",
    headers = {},
    query,
    body,
    authToken,
    signal,
  } = options;

  const url =
    input.startsWith("http://") || input.startsWith("https://")
      ? new URL(input)
      : new URL(input, typeof window === "undefined" ? "http://localhost" : window.location.origin);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      url.searchParams.set(key, String(value));
    });
  }

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };

  const init: RequestInit = {
    method,
    headers: finalHeaders,
    signal,
  };

  if (authToken) {
    finalHeaders.Authorization = `Bearer ${authToken}`;
  }

  if (body !== undefined && body !== null && method !== "GET") {
    init.body = body instanceof FormData ? body : JSON.stringify(body);
    if (!(body instanceof FormData) && !finalHeaders["Content-Type"]) {
      finalHeaders["Content-Type"] = "application/json";
    }
  }

  const response = await fetch(url.toString(), init);

  if (!response.ok) {
    const errorText = await readResponseText(response);
    let message = errorText || `Request to ${url.pathname} failed with status ${response.status}`;

    if (errorText) {
      try {
        const parsed = JSON.parse(errorText) as { error?: unknown; message?: unknown; errors?: unknown };
        const candidate =
          typeof parsed.error === "string"
            ? parsed.error
            : typeof parsed.message === "string"
              ? parsed.message
              : Array.isArray(parsed.errors) && parsed.errors.length > 0
                ? extractFirstErrorMessage(parsed.errors[0])
                : null;
        if (candidate && typeof candidate === "string" && candidate.trim().length > 0) {
          message = candidate;
        }
      } catch {
        // Ignore JSON parse errors; fall back to raw text.
      }
    }

    const error = new Error(message);
    (error as Error & { status?: number; responseText?: string }).status = response.status;
    (error as Error & { status?: number; responseText?: string }).responseText = errorText ?? undefined;
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

async function readResponseText(response: Response): Promise<string | null> {
  try {
    return await response.text();
  } catch {
    return null;
  }
}

function extractFirstErrorMessage(entry: unknown): string | null {
  if (typeof entry === "string") {
    return entry;
  }
  if (entry && typeof entry === "object") {
    const record = entry as { message?: unknown; detail?: unknown };
    if (typeof record.message === "string") return record.message;
    if (typeof record.detail === "string") return record.detail;
  }
  return null;
}
